// hooks/usePlanTracker.js - PROMPT-ONLY, NO MP4 DOWNLOADS

import { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { getPlanLimits, PLAN_LIMITS } from '../constants/planLimits';

export const usePlanTracker = (userId) => {
  const [userPlan, setUserPlan] = useState('free');
  const [usage, setUsage] = useState({
    prompt: 0
  });
  const [lastResetDate, setLastResetDate] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const initializeUsage = async () => {
    if (!userId) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const usageRef = doc(db, 'usage', userId);
      const usageDoc = await getDoc(usageRef);

      if (usageDoc.exists()) {
        const data = usageDoc.data();

        setUserPlan(data.planType || 'free');
        setUsage({
          prompt: data.monthlyUsage?.prompt || 0
        });
        setLastResetDate(data.lastResetDate?.toDate() || new Date());

        // ✅ ONLY reset FREE users (paid users handled by backend webhooks)
        if (data.planType === 'free') {
          await checkFreeUserReset(data);
        }

      } else {
        const newUsageDoc = {
          userId,
          planType: 'free',
          monthlyUsage: { prompt: 0 },
          lastResetDate: serverTimestamp(),
          createdAt: serverTimestamp()
        };

        await setDoc(usageRef, newUsageDoc);
        setUserPlan('free');
        setUsage({ prompt: 0 });
        setLastResetDate(new Date());
      }

    } catch (err) {
      setError(err.message || 'Failed to load usage data');
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ SIMPLE: Only check FREE users for 30-day reset
  const checkFreeUserReset = async (currentData) => {
    if (!currentData.lastResetDate) return;

    const lastReset = currentData.lastResetDate.toDate();
    const now = new Date();
    const daysSinceReset = Math.floor((now - lastReset) / (1000 * 60 * 60 * 24));

    // Reset if 30+ days
    if (daysSinceReset >= 30) {
      const usageRef = doc(db, 'usage', userId);
      await updateDoc(usageRef, {
        monthlyUsage: { prompt: 0 },
        lastResetDate: serverTimestamp()
      });

      setUsage({ prompt: 0 });
      setLastResetDate(new Date());
    }
  };

  const canCreateProject = () => {
    const limits = getPlanLimits(userPlan);
    const typeLimit = limits.monthlyProjects.prompt;
    const typeUsed = usage.prompt || 0;
    const typeRemaining = typeLimit - typeUsed;

    if (typeUsed >= typeLimit) {
      let upgradeMsg = '';

      if (userPlan === 'free') {
        upgradeMsg = `Upgrade to Starter for 60 prompt projects/month!`;
      } else if (userPlan === 'starter') {
        upgradeMsg = `Upgrade to Creator for 150 prompt projects/month!`;
      } else if (userPlan === 'creator') {
        upgradeMsg = `Upgrade to Professional for 250 prompt projects/month!`;
      }

      return {
        allowed: false,
        reason: `${userPlan.charAt(0).toUpperCase() + userPlan.slice(1)} plan limit reached (${typeLimit}/month). ${upgradeMsg}`,
        remaining: 0,
        limit: typeLimit,
        used: typeUsed
      };
    }

    return {
      allowed: true,
      reason: `You have ${typeRemaining} project${typeRemaining !== 1 ? 's' : ''} remaining this month`,
      remaining: typeRemaining,
      limit: typeLimit,
      used: typeUsed
    };
  };

  // ⚠️ Does NOT write to Firestore. The backend (checkLimits.js /
  // incrementUsage) is the only place monthly usage gets incremented —
  // it runs server-side right after a generation succeeds. This used to
  // also write 'monthlyUsage.prompt' directly from the client, which
  // meant if this ever got called anywhere, usage would be counted
  // twice (once here, once on the server) for the same generation.
  // Call this after a generation completes to pull the real,
  // server-authoritative count instead of guessing at it locally.
  const incrementUsage = async () => {
    if (!userId) return;
    await initializeUsage();
  };

  const updateUserPlan = async (newPlan) => {
    if (!userId) return;

    try {
      const usageRef = doc(db, 'usage', userId);

      await updateDoc(usageRef, {
        planType: newPlan,
        updatedAt: serverTimestamp()
      });

      setUserPlan(newPlan);
    } catch (err) {
      throw err;
    }
  };

  const getRemainingProjects = () => {
    const limits = getPlanLimits(userPlan);
    const promptRemaining = Math.max(0, limits.monthlyProjects.prompt - usage.prompt);

    return {
      prompt: promptRemaining
    };
  };

  const getUsagePercentage = () => {
    const limits = getPlanLimits(userPlan);
    const used = usage.prompt || 0;
    return Math.round((used / limits.monthlyProjects.prompt) * 100);
  };

  const shouldShowUpgradePrompt = () => {
    return getUsagePercentage() >= 80;
  };

  const getPlanInfo = () => {
    return PLAN_LIMITS[userPlan] || PLAN_LIMITS.free;
  };

  useEffect(() => {
    if (userId) {
      initializeUsage();
    } else {
      setUserPlan('free');
      setUsage({ prompt: 0 });
      setIsLoading(false);
    }
  }, [userId]);

  return {
    userPlan,
    usage,
    lastResetDate,
    isLoading,
    error,
    canCreateProject,
    getRemainingProjects,
    getUsagePercentage,
    shouldShowUpgradePrompt,
    getPlanInfo,
    incrementUsage,
    updateUserPlan,
    refresh: initializeUsage
  };
};

export default usePlanTracker;